import {storeInstance} from '@aofl/store/dist/legacy';
import {
  activationStartType,
  licenseTypes,
  sdoNs
} from './constants-enumerate';
import {deepAssign} from '@aofl/object-utils';
import {
  accessibleProductLabels,
  generalLabels,
  licenseHomeAccessTypeLabels
} from './i18n-labels';
import {until} from 'lit-html/directives/until';

const sdo = {
  namespace: sdoNs.LICENSES,
  mutations: {
    init() {
      return {
        queryObj: {
          sort: {},
          filters: {},
          persistent_selection: [],
        },
        searchTargetUrl: '',
        tableConfig: [],
        hasMore: false,
        licenses: [],
        loading: false,
        details: null,
        currentGroupLicenseCount: null,
        childGroupsLicenseCount: null,
        loadingDetails: false,
        distributionLog: [],
        assignmentLog: [],
        licenseAllocations: [],
        licenseAllocationGroupPathsRetrieved: [],
      };
    },
    updateSortOrder(subState, {index, field}) {
      let direction = subState.queryObj.sort.direction;
      if (direction === 'asc') {
        direction = 'desc';
      } else if (direction === 'desc') {
        direction = '';
      } else {
        direction = 'asc';
      }

      let fields = [field, 'id'];
      if (direction === '') fields = ['id'];

      return deepAssign(subState, 'queryObj.sort', {
        direction,
        fields: fields
      });
    },
    setDetails(subState, details) {
      return deepAssign(subState, '', {
        details: details.license_details,
        childGroupsLicenseCount: details.child_group_license_count,
        currentGroupLicenseCount: details.current_group_license_count,
      });
    },
  },
  decorators: [
    (nextState) => { // $licenses
      const currState = storeInstance.getState()[sdoNs.LICENSES];
      if (
        typeof nextState[sdoNs.LICENSES].$licenses === 'undefined' ||
        currState.licenses !== nextState[sdoNs.LICENSES].licenses
      ) {
        const $licenses = nextState[sdoNs.LICENSES].licenses.map((item) => {
          const licenseType = (item.expiration_type === 'duration') ?
            until(generalLabels.DurationLabel) :
            (item.expired) ?
              until(generalLabels.FixedEndDateExpiredLabel) :
              until(generalLabels.FixedEndDateLabel);
          return {
            id: item.id,
            license_name: item.name,
            license_type: licenseType,
            product: accessibleProductLabels[item.product],
            home_access_type: licenseHomeAccessTypeLabels[item.access_type],
            status: item.status,
            total: item.total,
            available: item.available,
            redeemed: item.redeemed,
            assigned: item.assigned,
            group_total: item.group_total,
            group_available: item.group_available,
            group_assigned: item.group_assigned,
            transferable: (item.transferable) ? until(generalLabels.Yes) : until(generalLabels.No),
            expired: item.expired,
            fspk: item.fspk
          };
        });
        nextState = deepAssign(nextState, sdoNs.LICENSES, {$licenses});
      }
      return nextState;
    },
    (nextState) => { // $orderedLicenseAllocations
      const currState = storeInstance.getState()[sdoNs.LICENSES];
      if (
          typeof nextState[sdoNs.LICENSES].$orderedLicenseAllocations === 'undefined' ||
          currState.licenseAllocations !== nextState[sdoNs.LICENSES].licenseAllocations
      ) {
        const $orderedLicenseAllocations = {};
        nextState[sdoNs.LICENSES].licenseAllocations.map((allocation) => {

          let fullPath = allocation.group_path;
          if (fullPath === '') {
            fullPath = allocation.group_id + '.';
          }
          if (typeof $orderedLicenseAllocations[fullPath] === 'undefined') {
            $orderedLicenseAllocations[fullPath] = [];
          }
          $orderedLicenseAllocations[fullPath].push(allocation);
        });
        nextState = deepAssign(nextState, sdoNs.LICENSES, {$orderedLicenseAllocations});

      }
      return nextState;
    },
    (nextState) => { // prev search query
      const state = storeInstance.getState();
      const nextSubState = nextState[sdoNs.LICENSES];
      const subState = state[sdoNs.LICENSES];

      if (typeof nextSubState.$prevQueryObj === 'undefined' || nextSubState.queryObj !== subState.queryObj) {
        const $prevQueryObj = subState.queryObj;

        nextState = deepAssign(nextState, `${sdoNs.LICENSES}`, {$prevQueryObj});
      }

      return nextState;
    },
    (nextState) => { // prev pointer items
      const state = storeInstance.getState();
      const nextSubState = nextState[sdoNs.LICENSES];
      const subState = state[sdoNs.LICENSES];

      if (typeof nextSubState.$prev === 'undefined' || nextSubState.licenses !== subState.licenses) {
        let $prev = '';
        if (nextSubState.licenses.length > 0) {
          $prev = nextSubState.licenses[0]; /** @todo assign needed props */
        }

        nextState = deepAssign(nextState, `${sdoNs.LICENSES}`, {$prev});
      }

      return nextState;
    },
    (nextState) => { // next pointer items
      const state = storeInstance.getState();
      const nextSubState = nextState[sdoNs.LICENSES];
      const subState = state[sdoNs.LICENSES];

      if (typeof nextSubState.$next === 'undefined' ||
        nextSubState.licenses !== subState.licenses) {
        let $next = '';
        if (nextSubState.licenses.length > 0) {
          $next = nextSubState.licenses[nextSubState.licenses.length - 1]; /** @todo assign needed props */
        }

        nextState = deepAssign(nextState, `${sdoNs.LICENSES}`, {$next});
      }

      return nextState;
    },
    (nextState) => { // $sort to make api call
      const state = storeInstance.getState();

      const nextSubState = nextState[sdoNs.LICENSES];
      const subState = state[sdoNs.LICENSES];

      if (typeof nextSubState.$sort === 'undefined' ||
        nextSubState.queryObj.sort !== subState.queryObj.sort) {
        const $sort = Object.assign({}, nextSubState.queryObj.sort);
        if ($sort.direction === '') {
          $sort.direction = 'asc';
        }

        nextState = deepAssign(nextState, sdoNs.LICENSES, {
          $sort
        });
      }

      return nextState;
    },
    (nextState) => { // $pageNumber
      const state = storeInstance.getState();
      const nextSubState = nextState[sdoNs.LICENSES];
      const subState = state[sdoNs.LICENSES];

      if (
        typeof nextSubState.$pageNumber === 'undefined'
        ||
        nextSubState.hasMore !== subState.hasMore
        ||
        nextSubState.$sort !== subState.$sort
      ) {
        const hasOffset = Boolean(nextSubState.$sort.last_values);
        let $pageNumber = 1;
        let $numPages = 3;

        if (nextSubState.$sort.is_forward) {
          if (hasOffset) {
            $pageNumber = 2;
          }

          if (!nextSubState.hasMore) {
            $numPages = $pageNumber;
          }
        } else {
          $pageNumber = nextSubState.hasMore ? 2 : 1;
        }

        nextState = deepAssign(nextState, `${sdoNs.LICENSES}`, {$pageNumber, $numPages});
      }

      return nextState;
    },
    (nextState) => { // $licensesByProduct
      const currState = storeInstance.getState()[sdoNs.LICENSES];
      if (
        typeof nextState[sdoNs.LICENSES].$licensesByProduct === 'undefined' ||
        currState.licenses !== nextState[sdoNs.LICENSES].licenses
      ) {
        const $licensesByProduct = {};
        const licenses = nextState[sdoNs.LICENSES].licenses;

        for (let i = 0; i < licenses.length; i++) {
          const license = licenses[i];

          if (typeof $licensesByProduct[license.product] === 'undefined') {
            $licensesByProduct[license.product] = [];
          }

          $licensesByProduct[license.product].push({
            id: license.id,
            license_name: license.name,
            license_type: licenseTypes[license.type],
            expiration_type: license.expiration_type,
            transferable: license.transferable,
            fspk: license.fspk,
            home_access_type: licenseHomeAccessTypeLabels[license.access_type],
            status: license.status,
            total: license.total,
            available: license.available,
            test: license.test || false,
            expired: license.expired,
            group_total: license.group_total,
            group_available: license.group_available,
            group_assigned: license.group_assigned,
          });
        }

        nextState = deepAssign(nextState, sdoNs.LICENSES, {$licensesByProduct});
      }
      return nextState;
    },
    (nextState) => { // $summary
      const currState = storeInstance.getState()[sdoNs.LICENSES];
      if (
        typeof nextState[sdoNs.LICENSES].$summary === 'undefined' ||
        currState.licenses !== nextState[sdoNs.LICENSES].licenses
      ) {
        const $summary = {};
        nextState[sdoNs.LICENSES].licenses.forEach((license) => {
          if (!$summary[license.product]) {
            $summary[license.product] = {
              total: 0,
              assigned: 0,
              available: 0
            };
          }
          $summary[license.product].total += license.total;
          $summary[license.product].available += license.available;
          $summary[license.product].assigned += license.assigned;
        });
        nextState = deepAssign(nextState, sdoNs.LICENSES, {$summary});
      }
      return nextState;
    },
    (nextState) => { // $details
      const currState = storeInstance.getState()[sdoNs.LICENSES];
      if (
        typeof nextState[sdoNs.LICENSES].$details === 'undefined' ||
        currState.details !== nextState[sdoNs.LICENSES].details
      ) {
        const details = nextState[sdoNs.LICENSES].details || {
          id: 0,
          name: '',
          access_data: {
            expiration: '',
            refundable: '',
            expiration_type: '',
            activation_start: null,
            external_access_type: '',
            activation_start_type: '',
            auto_license: false,
          },
          aofl_product: '',
          type: ''

        };
        const $details = {
          id: Number(details.id) || 0,
          activation_start_type: details.access_data?.activation_start_type,
          product: details.aofl_product || '',
          access_type: details.access_data?.external_access_type || '',
          test: details.access_data.test || false,
          type: details.type || '',
          name: details.name || '',
          fixed_duration: '',
          fixed_end_date: '',
          start_date: '',
          expired: details.expired || false,
          transferable: details.access_data?.transferable || false,
          fspk: details.access_data?.fspk || false,
          auto_license: details.access_data?.auto_license || false
        };

        if (details.access_data.expiration_type === 'duration') {
          $details.fixed_duration = details.access_data.expiration;
        } else if (details.access_data.expiration_type === 'fixed_end_date') {
          $details.fixed_end_date = new Date(parseInt(details.access_data.expiration, 10) * 1000).toLocaleDateString();
          $details.start_date = new Date(
            parseInt(details.access_data.activation_start, 10) * 1000
          ).toLocaleDateString();
        }

        nextState = deepAssign(nextState, sdoNs.LICENSES, {$details});
      }
      return nextState;
    },
    (nextState) => { // $distributionLog
      const state = storeInstance.getState();
      const currState = state[sdoNs.LICENSES];
      const nextSessionState = nextState[sdoNs.SESSION];
      if (
        typeof nextState[sdoNs.LICENSES].$distributionLog === 'undefined' ||
        currState.distributionLog !== nextState[sdoNs.LICENSES].distributionLog ||
        state[sdoNs.SESSION].locale !== nextState[sdoNs.SESSION].locale
      ) {
        const dateOptions = {year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'};
        const $distributionLog = nextState[sdoNs.LICENSES].distributionLog.map((item) =>
          Object.assign({}, item, {
            date: new Date(item.timestamp * 1000).toLocaleDateString(nextState[sdoNs.SESSION].locale, dateOptions),
            $toGroupLabel: nextSessionState.settings.tier_structure.labels[item.to_group_level - 2],
          })
        );
        nextState = deepAssign(nextState, sdoNs.LICENSES, {$distributionLog});
      }
      return nextState;
    },
    (nextState) => { // $assessmentLog
      const state = storeInstance.getState();
      const currState = state[sdoNs.LICENSES];
      if (
        typeof nextState[sdoNs.LICENSES].$assignmentLog === 'undefined' ||
        currState.assignmentLog !== nextState[sdoNs.LICENSES].assignmentLog ||
        state[sdoNs.SESSION].locale !== nextState[sdoNs.SESSION].locale
      ) {
        const dateOptions = {year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'};
        const $assignmentLog = nextState[sdoNs.LICENSES].assignmentLog.map((item) =>
          Object.assign({}, item, {
            date: new Date(item.timestamp * 1000).toLocaleDateString(nextState[sdoNs.SESSION].locale, dateOptions)
          })
        );
        nextState = deepAssign(nextState, sdoNs.LICENSES, {$assignmentLog});
      }
      return nextState;
    },
    (nextState) => { // $maxLevel
      const state = storeInstance.getState();
      const subState = state[sdoNs.SESSION];
      const nextSubState = nextState[sdoNs.SESSION];

      if (typeof nextState.$maxLevel === 'undefined' ||
      nextSubState.settings.tier_structure.levels !== subState.settings.tier_structure.levels) {
        const $maxLevel = parseInt(nextSubState.settings.tier_structure.levels, 10);

        nextState = deepAssign(nextState, sdoNs.LICENSES, {$maxLevel});
      }

      return nextState;
    },
    (nextState) => { // $licenseAllocations
      const state = storeInstance.getState();
      const subState = state[sdoNs.SESSION];
      const nextSubState = nextState[sdoNs.SESSION];

      if (typeof nextState.$licenseAllocations === 'undefined' ||
      nextSubState.licenseAllocations !== subState.licenseAllocations) {
        const $licenseAllocations = [];
        nextState = deepAssign(nextState, sdoNs.LICENSES, {$licenseAllocations});
      }

      return nextState;
    }
  ]
};

storeInstance.addState(sdo);
